'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStage {
  id: number;
  stage: string;
  title: string;
  duration: string;
  description: string;
  keyActivities: string[];
  deliverables: string[];
}

interface InvestmentProcessProps {
  className?: string;
}

const InvestmentProcess = ({ className = '' }: InvestmentProcessProps) => {
  const [activeStage, setActiveStage] = useState<number>(1);

  const processStages: ProcessStage[] = [
    {
      id: 1,
      stage: 'Stage 1',
      title: 'Opportunity Identification',
      duration: '2-3 months',
      description: 'Systematic screening of sectors and opportunities using demographic analysis, infrastructure gap assessment, and policy environment evaluation.',
      keyActivities: [
        'Sector analysis and prioritization',
        'Market opportunity sizing',
        'Preliminary stakeholder mapping',
        'Initial risk assessment'
      ],
      deliverables: [
        'Sector opportunity brief',
        'Investment thesis outline',
        'Preliminary financial model'
      ]
    },
    {
      id: 2,
      stage: 'Stage 2',
      title: 'Due Diligence & Structuring',
      duration: '4-6 months',
      description: 'Comprehensive analysis of financial, operational, legal, and impact dimensions with sophisticated capital structure design.',
      keyActivities: [
        'Financial and operational due diligence',
        'Legal and regulatory compliance review',
        'Impact assessment framework development',
        'Capital structure optimization'
      ],
      deliverables: [
        'Due diligence report',
        'Investment structure proposal',
        'Risk mitigation framework',
        'Impact measurement plan'
      ]
    },
    {
      id: 3,
      stage: 'Stage 3',
      title: 'Partnership Development',
      duration: '3-4 months',
      description: 'Deep engagement with government, local enterprises, and technical partners to ensure alignment and sustainable implementation.',
      keyActivities: [
        'Government stakeholder engagement',
        'Local partner identification and vetting',
        'Technical assistance provider selection',
        'Community consultation protocols'
      ],
      deliverables: [
        'Partnership agreements',
        'Government MOU documentation',
        'Technical assistance framework',
        'Community engagement plan'
      ]
    },
    {
      id: 4,
      stage: 'Stage 4',
      title: 'Investment Committee Approval',
      duration: '1-2 months',
      description: 'Rigorous internal review process ensuring alignment with investment criteria, risk parameters, and impact objectives.',
      keyActivities: [
        'Investment committee presentation',
        'Risk committee review',
        'Impact committee assessment',
        'Final terms negotiation'
      ],
      deliverables: [
        'Investment committee memo',
        'Final investment agreement',
        'Approved financial model',
        'Implementation roadmap'
      ]
    },
    {
      id: 5,
      stage: 'Stage 5',
      title: 'Deployment & Monitoring',
      duration: 'Ongoing',
      description: 'Phased capital deployment with continuous performance monitoring, impact tracking, and adaptive management.',
      keyActivities: [
        'Phased capital deployment',
        'Quarterly performance monitoring',
        'Impact metrics tracking',
        'Risk management oversight'
      ],
      deliverables: [
        'Quarterly performance reports',
        'Impact assessment updates',
        'Risk monitoring dashboards',
        'Stakeholder communication materials'
      ]
    }
  ];

  return (
    <section data-header="light" className={`py-4 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mb-2 sm:mb-4 md:mb-6">
          <h2 className="font-headline text-3xl lg:text-5xl font-semibold text-foreground mb-2 sm:mb-4">
            <span className="text-accent">Investment Process</span>: From Identification to Impact
          </h2>
          <p className="font-body text-base sm:text-lg text-text-secondary leading-relaxed">
            Our systematic five-stage process ensures thorough analysis, stakeholder alignment, and sustainable implementation for every investment opportunity.
          </p>
        </div>

        <div className="mb-2 sm:mb-4 md:mb-6">
          <div className="flex flex-wrap gap-1 sm:gap-4 ">
            {processStages.map((stage) => (
              <button
                key={stage.id}
                onClick={() => setActiveStage(stage.id)}
                className={`px-2 sm:px-6 py-1 sm:py-3 rounded-lg font-body text-sm font-medium transition-all duration-300 ${
                  activeStage === stage.id
                    ? 'bg-primary text-white shadow-institutional'
                    : 'bg-card text-text-secondary hover:bg-primary/10 border border-border'
                }`}
              >
                {stage.stage}
              </button>
            ))}
          </div>
        </div>

        {processStages.map((stage) => (
          <div
            key={stage.id}
            className={`${activeStage === stage.id ? 'block' : 'hidden'}`}
          >
            <div className="bg-card border border-border rounded-lg overflow-hidden shadow-institutional">
              <div className="bg-gradient-to-r from-primary to-brand-forest px-4 sm:px-8 py-3 sm:py-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h3 className="font-body text-xl sm:text-2xl font-semibold text-white mb-2">
                      {stage.title}
                    </h3>
                    <p className="font-body text-sm text-gray-200">
                      {stage.stage} • {stage.duration}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-8">
                <p className="font-body text-base text-text-secondary leading-relaxed mb-2 sm:mb-4">
                  {stage.description}
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                  <div>
                    <h4 className="font-body text-sm font-semibold text-text-primary uppercase tracking-wide mb-2 sm:mb-4 flex items-center space-x-2">
                      <Icon name="ClipboardDocumentListIcon" size={15} variant="outline" className="text-primary" />
                      <span>Key Activities</span>
                    </h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {stage.keyActivities.map((activity, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                          <span className="font-body text-sm text-text-secondary">{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-body text-sm font-semibold text-text-primary uppercase tracking-wide mb-4 flex items-center space-x-2">
                      <Icon name="DocumentTextIcon" size={15} variant="outline" className="text-accent" />
                      <span>Key Deliverables</span>
                    </h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {stage.deliverables.map((deliverable, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="DocumentCheckIcon" size={18} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="font-body text-sm text-text-secondary">{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default InvestmentProcess;